import { useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import {
  AudioMutedOutlined,
  AudioOutlined,
  PhoneOutlined,
  SelectOutlined,
} from "@ant-design/icons";
import classNames from "classnames";
import {
  ACTIVE_CALL_PHASES,
  PATIENT_CALL_PHASE_LABELS,
  formatCallDuration,
  patientCallManager,
} from "../../../pages/CompassusEligibility/patientCallManager";
import { formatDialpadPhone } from "../../../pages/CompassusEligibility/PatientCallDialer";
import { useAppNavigate } from "../../../hooks/router";
import styles from "./styles.module.scss";

const WAVE_FACTORS = [0.55, 0.9, 0.7, 1, 0.6];

/**
 * Floating top-center pill for an in-progress patient call that outlived the
 * view that started it (Communication tab / profile drawer). Lets the user
 * navigate freely: shows live audio activity, duration, mute and hang-up
 * controls, and routes back to the call's Communication tab. Renders above
 * drawers (z 1000) and the visit workspaces (z 1199); hides while the call's
 * own dialer view is on screen.
 */
export const CompassusActiveCallPill = () => {
  const snapshot = useSyncExternalStore(
    patientCallManager.subscribe,
    patientCallManager.getSnapshot,
    patientCallManager.getSnapshot
  );
  const navigate = useAppNavigate();
  const context = snapshot.context;
  const isCallActive =
    context?.persist === true && ACTIVE_CALL_PHASES.includes(snapshot.phase);
  const isDialerVisible = context
    ? snapshot.visibleDialerOwnerKeys.includes(context.ownerKey)
    : false;
  if (!context || !isCallActive || isDialerVisible) return null;

  const isConnected =
    snapshot.phase === "connected" || snapshot.phase === "reconnecting";
  const waveLevel = Math.max(snapshot.volume.input, snapshot.volume.output);
  const route = context.route;

  const goToCall = () => {
    if (!route) return;
    navigate(`/compassus-patient/${route.patientId}/communication`);
  };

  return createPortal(
    <div className={styles.pillWrapper}>
      <div
        className={styles.pill}
        role="status"
        aria-live="polite"
        aria-label={`Ongoing call with ${context.displayName}`}
      >
        <span
          className={classNames(styles.wave, {
            [styles.waveIdle]: !isConnected || snapshot.isMuted,
          })}
          aria-hidden="true"
        >
          {WAVE_FACTORS.map((factor, index) => (
            <span
              key={index}
              style={{
                height: isConnected
                  ? `${4 + waveLevel * factor * 12}px`
                  : undefined,
              }}
            />
          ))}
        </span>
        <button
          type="button"
          className={styles.identity}
          onClick={goToCall}
          disabled={!route}
          title={
            route ? "Open this call's Communication tab" : context.displayName
          }
        >
          <span className={styles.name}>{context.displayName}</span>
          <span className={styles.detail}>
            {formatDialpadPhone(context.phoneNumber)}
          </span>
        </button>
        <span className={styles.duration}>
          {isConnected
            ? formatCallDuration(snapshot.elapsedSeconds)
            : PATIENT_CALL_PHASE_LABELS[snapshot.phase]}
        </span>
        <button
          type="button"
          className={classNames(styles.controlButton, {
            [styles.controlButtonActive]: snapshot.isMuted,
          })}
          onClick={() => patientCallManager.toggleMute()}
          disabled={!isConnected}
          aria-label={snapshot.isMuted ? "Unmute" : "Mute"}
          aria-pressed={snapshot.isMuted}
        >
          {snapshot.isMuted ? <AudioMutedOutlined /> : <AudioOutlined />}
        </button>
        <button
          type="button"
          className={classNames(styles.controlButton, styles.hangUpButton)}
          onClick={() => void patientCallManager.hangUp()}
          aria-label="Hang up"
        >
          {/* Receiver-down orientation, matching the telehealth end-call button. */}
          <PhoneOutlined rotate={225} />
        </button>
        {route ? (
          <button
            type="button"
            className={styles.controlButton}
            onClick={goToCall}
            aria-label="Go to this call's Communication tab"
          >
            <SelectOutlined />
          </button>
        ) : null}
      </div>
    </div>,
    document.body
  );
};

export default CompassusActiveCallPill;
